import { useMemo, useState } from "react";
import { SearchIcon } from "../../icons";
import GuideHint from "../GetStartedGuide/GuideHint";
import { useSelector } from "react-redux";
import { FormatBalance, GetCurrenySymbol } from "../../../helper";

const FiatAccountRow = ({ account, isFirst, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect?.(account)}
    className={`flex h-[70px] w-full shrink-0 items-center justify-between rounded-[12px] bg-background-alt py-[15px] text-left custom-transition hover:bg-option-hover ${
      isFirst ? "px-3.5" : "px-3"
    }`}
  >
    <div
      className={`flex min-w-0 items-center ${isFirst ? "gap-3" : "gap-2.5"}`}
    >
      <div
        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full"
        style={account.iconBg ? { backgroundColor: account.iconBg } : undefined}
      >
        <img
          src={account.logo}
          alt={account.currency_code}
          className="size-9 rounded-full object-cover"
        />
      </div>
      <div className="min-w-0">
        <p className="whitespace-pre-wrap text-sm font-semibold leading-[19.5px] text-text-primary">
          {account.title}
        </p>
        <p className="text-xs font-normal leading-[16.5px] text-text-secondary">
          {account.account_number}
        </p>
      </div>
    </div>
    <p className="flex items-center justify-center space-x-1 shrink-0 whitespace-nowrap text-sm font-medium leading-[19.5px] text-text-primary">
      <span>{GetCurrenySymbol(account.currency_code, 12, 12)}</span>
      <span>{FormatBalance(account.balance)}</span>
    </p>
  </button>
);

const SelectFiatAccountStep = ({
  onAccountSelect,
  title = "Select Account",
  sectionLabel = "Your Accounts",
  placeholder = "Search Account...",
  spotlightActive = false,
  guideHint = null,
}) => {
  const [search, setSearch] = useState("");
  const accounts = useSelector((state) => state.client.clientInfo.accounts);
  const fiatAccounts = accounts.filter(account => (account.is_crypto === null | false) && (account.is_crypto_supported === true));


  const filteredAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return fiatAccounts;

    return fiatAccounts.filter(
      (account) =>
        account.title.toLowerCase().includes(query) ||
        account.currency_code.toLowerCase().includes(query) ||
        account.account_number.includes(query),
    );
    // eslint-disable-next-line
  }, [search]);

  const accountsSection = (
    <div className="flex shrink-0 flex-col gap-1">
      {/* <p className="shrink-0 text-sm font-medium text-text-primary">{sectionLabel}</p> */}
      <div className="flex max-h-[450px] flex-col gap-2 overflow-y-auto pr-1.5 custom-scrollbar">
        {filteredAccounts.map((account, index) => (
          <FiatAccountRow
            key={account.id}
            account={account}
            isFirst={index === 0}
            onSelect={onAccountSelect}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div
      className={`flex flex-col sm:gap-4 px-4 pt-6 sm:h-full sm:min-h-0 gap-6 sm:pt-4 ${
        spotlightActive ? "max-sm:shrink-0" : "h-full min-h-0"
      }`}
    >
      <h3 className="shrink-0 px-2 text-base font-semibold text-text-primary sm:text-lg">
        {title}
      </h3>

      <div className="flex h-10 w-full shrink-0 items-center gap-2 rounded-[10px] border border-divider bg-background-alt py-2.5 pl-4 pr-3">
        <SearchIcon />
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={placeholder}
          className="w-full border-0 bg-transparent p-0 text-xs font-normal leading-normal text-text-primary placeholder:text-text-secondary outline-none focus:border-0 focus:outline-none focus:ring-0"
        />
      </div>

      {spotlightActive ? (
        <div className="relative z-40 shrink-0 max-sm:-mx-4 max-sm:rounded-t-[12px] max-sm:bg-background-surface max-sm:px-4 max-sm:pb-4 max-sm:pt-4 sm:min-h-0 sm:flex-1 sm:rounded-none sm:bg-transparent sm:p-0">
          {guideHint && (
            <div className="pointer-events-none absolute inset-x-4 bottom-full z-50 mb-2 sm:hidden">
              <div className="pointer-events-auto mx-auto w-[90%] max-w-[280px]">
                <GuideHint
                  title={guideHint.title}
                  description={guideHint.description}
                  primaryAction={guideHint.primaryAction}
                  secondaryAction={guideHint.secondaryAction}
                  onSkip={guideHint.onSkip}
                  onDone={guideHint.onDone}
                  onDepositCrypto={guideHint.onDepositCrypto}
                />
              </div>
            </div>
          )}
          {accountsSection}
        </div>
      ) : (
        accountsSection
      )}
    </div>
  );
};

export default SelectFiatAccountStep;
